import { app } from "api";
import { useMutation, useQuery } from "react-query";
import firebase from "firebase";
import imageCompression from "browser-image-compression";

export async function signInWithEmailAndPassword(
  email: string,
  password: string
) {
  return await app.auth().signInWithEmailAndPassword(email, password);
}

export async function signOut() {
  return unregisterWebNotificationToken().then(async () => {
    return await app.auth().signOut();
  });
}

const createLabAccount = app.functions().httpsCallable("authGroup-createLab");

export async function signUp(email: string, password: string) {
  try {
    const token = await createLabAccount({ email, password });
    await app.auth().signInWithCustomToken(token.data);
    registerWebNotification();
  } catch (e) {
    console.error("error in signup", e);
  }
}

export function useProfileUpdate() {
  return useMutation(async (values: any) => {
    const uid = app.auth().currentUser!.uid;
    const images = values.image;
    delete values.image;
    if (images.length > 0) {
      const compressedImage = await imageCompression(images[0], {
        maxSizeMB: 0.2,
        useWebWorker: true,
      });
      let upload = await app
        .storage()
        .ref()
        .child("labs")
        .child(uid)
        .child("profilePictures")
        .child(compressedImage.name)
        .put(compressedImage);

      let url = await upload.ref.getDownloadURL();
      values.photoUrl = url;
    }

    if (values.location) {
      values.lat = values.location.lat;
      values.long = values.location.lng;
    }

    return await app.firestore().collection("labs").doc(uid).update(values);
  });
}

export async function completeSignup(values: any) {
  //TODO Cleanup
  const uid = app.auth().currentUser!.uid;

  const filesUploadPromises = Array.from(values.verificationDocuments).map(
    (doc: any) => {
      return app
        .storage()
        .ref()
        .child("labs")
        .child(uid)
        .child("documents")
        .child(doc.name)
        .put(doc)
        .then((snap) =>
          snap.ref
            .getDownloadURL()
            .then((url) => ({ name: doc.name, url: url }))
        );
    }
  );

  const verificationDocuments = await Promise.all(filesUploadPromises);

  const image = values.image;
  delete values.image;

  if (image.length > 0) {
    let upload = await app
      .storage()
      .ref()
      .child("labs")
      .child(uid)
      .child("profilePictures")
      .child(image[0].name)
      .put(image[0]);

    let url = await upload.ref.getDownloadURL();
    values.photoUrl = url;
  }

  if (values.location) {
    values.lat = values.location.lat;
    values.long = values.location.lng;
  }

  delete values.verificationDocuments;

  await app
    .firestore()
    .collection("labs")
    .doc(uid)
    .update({
      ...values,
      verificationDocuments,
      complete: true,
      tests: [],
      testCategories: [],
    });
}

export function useCompleteSignup() {
  return useMutation((values: any) => completeSignup(values));
}

// export async function signUp(email: string, password: string) {
//   try {
//     const creds = await app
//       .auth()
//       .createUserWithEmailAndPassword(email, password);
//     if (creds.user) {
//       const setLabClaims = app
//         .functions()
//         .httpsCallable("authGroup-setLabClaims");

//       console.log({ setLabClaims });
//       await setLabClaims({
//         uid: creds.user.uid,
//       });
//       console.log("done");
//     }
//   } catch (e) {
//     console.error("error in sifgnup", e);
//   }
// }

export async function deleteVerificationImage(image: any) {
  const uid = app.auth().currentUser!.uid;

  await app
    .firestore()
    .collection("labs")
    .doc(uid)
    .update({
      verificationDocuments: firebase.firestore.FieldValue.arrayRemove(image),
    });

  const imageRef = app.storage().refFromURL(image.url);

  await imageRef.delete();
}

export function useDeleteVerificationImage() {
  return useMutation((data) => deleteVerificationImage(data));
}
export async function deleteAdImage(image: any) {
  const uid = app.auth().currentUser!.uid;

  await app
    .firestore()
    .collection("labs")
    .doc(uid)
    .update({
      images: firebase.firestore.FieldValue.arrayRemove(image),
    });

  const imageRef = app.storage().refFromURL(image.url);

  await imageRef.delete();
}

export function useDeleteAdImage() {
  return useMutation((data) => deleteAdImage(data));
}

export async function addVerificationImage(file: any) {
  const uid = app.auth().currentUser!.uid;

  const fileInfo = await app
    .storage()
    .ref()
    .child("labs")
    .child(uid)
    .child("documents")
    .child(file.name)
    .put(file)
    .then((snap) =>
      snap.ref.getDownloadURL().then((url) => ({ name: file.name, url: url }))
    );

  await app
    .firestore()
    .collection("labs")
    .doc(uid)
    .update({
      verificationDocuments: firebase.firestore.FieldValue.arrayUnion(fileInfo),
    });
}
export async function addAdvertisementImage(file: any) {
  const uid = app.auth().currentUser!.uid;

  const fileInfo = await app
    .storage()
    .ref()
    .child("labs")
    .child(uid)
    .child("adImages")
    .child(file.name)
    .put(file)
    .then((snap) =>
      snap.ref.getDownloadURL().then((url) => ({ name: file.name, url: url }))
    );

  await app
    .firestore()
    .collection("labs")
    .doc(uid)
    .update({
      images: firebase.firestore.FieldValue.arrayUnion(fileInfo),
    });
}

async function getLocations() {
  return (await app.firestore().collection("locations").get()).docs.map((i) =>
    i.data()
  );
}
export function useLocations() {
  return useQuery(["locations"], getLocations);
}

export function registerWebNotification() {
  if (!firebase.messaging.isSupported()) {
    return;
  }
  const messaging = firebase.messaging();
  Notification.requestPermission()
    .then(function (res) {
      return messaging.getToken();
    })
    .then(function (tk) {
      updateLab({ token: tk });
    })
    .catch(function (err) {
      console.log("error", err);
    });
  messaging.onMessage(function (payload: any) {
    console.log("payloads notification", payload);
  });
}

async function unregisterWebNotificationToken() {
  updateLab({ token: null });
  await firebase.messaging().deleteToken();
}

export async function updateLab(labData: any) {
  const labId = app.auth().currentUser?.uid;
  await app.firestore().collection("labs").doc(labId).update(labData);
}
